import { useCallback, useEffect, useState } from 'react';

import { useAppDispatch } from 'src/app/store/hooks';
import {
  useLazyPassengersRetrieveQuery,
  useLazyPayersRetrieveQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

export function usePayerEmail(
  type: 'passenger' | 'payer_organization',
  payerId: string
) {
  const dispatch = useAppDispatch();
  const [fetchPassenger, { isLoading: isLoadingPassenger }] =
    useLazyPassengersRetrieveQuery();
  const [fetchPayer, { isLoading: isLoadingPayer }] =
    useLazyPayersRetrieveQuery();

  const isLoading = type === 'passenger' ? isLoadingPassenger : isLoadingPayer;

  const [email, setEmail] = useState<string>();

  const handleFetchPassengerEmail = useCallback(async () => {
    try {
      const passenger = await fetchPassenger({
        id: payerId,
      }).unwrap();

      setEmail(passenger.email);
    } catch (e) {
      dispatch(
        show({
          type: 'error',
          title: 'Unable to fetch passenger record',
          description: formatServerError(e),
        })
      );
    }
  }, [dispatch, fetchPassenger, payerId]);

  const handleFetchPayerEmail = useCallback(async () => {
    try {
      const payer = await fetchPayer({
        id: payerId,
      }).unwrap();

      setEmail(payer.billing_email);
    } catch (e) {
      dispatch(
        show({
          type: 'error',
          title: 'Unable to fetch payer record',
          description: formatServerError(e),
        })
      );
    }
  }, [dispatch, fetchPayer, payerId]);

  useEffect(() => {
    if (type === 'passenger') {
      handleFetchPassengerEmail();
    } else {
      handleFetchPayerEmail();
    }
  }, [handleFetchPassengerEmail, handleFetchPayerEmail, type]);

  return {
    isLoading,
    email,
  };
}
